// --- Images: Index ---
import BgAcceleratingEuropesGrowth from "../../../assets/images/content/pillar-2/index/bg-header-3.webp";
import Pillar2Consumer from "../../../assets/images/content/pillar-2/index/factory.webp";

// --- Images: Industrial Footprint ---
import UnitsTradedIntroDesktop from "../../../assets/images/content/pillar-2/industrial-footprint/units-traded-in-Europe-intro-desktop.svg";
import UnitsTradedIntroMobile from "../../../assets/images/content/pillar-2/industrial-footprint/units-traded-in-Europe-intro-mobile.svg";
import UnitsTradedSection1Desktop from "../../../assets/images/content/pillar-2/industrial-footprint/units-traded-in-Europe-section1-desktop.svg";
import UnitsTradedSection1Mobile from "../../../assets/images/content/pillar-2/industrial-footprint/units-traded-in-Europe-section1-mobile.svg";
import UnitsTradedSection2Desktop from "../../../assets/images/content/pillar-2/industrial-footprint/units-traded-in-Europe-section2-desktop.svg";
import UnitsTradedSection2Mobile from "../../../assets/images/content/pillar-2/industrial-footprint/units-traded-in-Europe-section2-mobile.svg";

// --- Images: Innovation ---
import InvestmentRDDesktop from "../../../assets/images/content/pillar-2/innovation/investment-in-r-and-d-by-eu-member-states-desktop.svg";
import InvestmentRDMobile from "../../../assets/images/content/pillar-2/innovation/investment-in-r-and-d-by-eu-member-states-mobile.svg";
import InvestmentGDPDesktop from "../../../assets/images/content/pillar-2/innovation/investment-in-rd-by-eu-member-states-gdp-desktop.svg";
import InvestmentGDPMobile from "../../../assets/images/content/pillar-2/innovation/investment-in-rd-by-eu-member-states-gdp-mobile.svg";

// --- KPI icons ---
import { ReactComponent as IconFactory } from "../../../assets/vectors/icons/kpi/factory.svg";
import { ReactComponent as IconShoppingCart } from "../../../assets/vectors/icons/kpi/shopping-cart.svg";
import { ReactComponent as IconPackage } from "../../../assets/vectors/icons/kpi/package.svg";
import { ReactComponent as IconFlask } from "../../../assets/vectors/icons/kpi/flask-conical.svg";
import { ReactComponent as IconChartPie } from "../../../assets/vectors/icons/kpi/chart-pie.svg";
import { ReactComponent as IconFileBadge } from "../../../assets/vectors/icons/kpi/file-badge.svg";
import { ReactComponent as IconGlobe } from "../../../assets/vectors/icons/kpi/globe.svg";
import { ReactComponent as IconShip } from "../../../assets/vectors/icons/kpi/ship.svg";
import { ReactComponent as IconMap } from "../../../assets/vectors/icons/kpi/map.svg";

// --- Images: Trade ---
import LargeAppliancesDesktop from "../../../assets/images/content/pillar-2/trade/large-appliances-sold-globally-desktop.svg";
import LargeAppliancesMobile from "../../../assets/images/content/pillar-2/trade/large-appliances-sold-globally-mobile.svg";

// --- Components ---
import { TradeShareLineChart } from "../../../components/CustomPageBlocks/pillar2/TradeShareLineChart/TradeShareLineChart";
import { EuropeanMarketHistoricalPicture } from "../../../components/CustomPageBlocks/pillar2/EuropeanMarketHistoricalPicture/EuropeanMarketHistoricalPicture";
import { EvolutionOfImportAndExportOfHomeAppliancesInEurope } from "../../../components/CustomPageBlocks/pillar2/EvolutionOfImportAndExportOfHomeAppliancesInEurope/EvolutionOfImportAndExportOfHomeAppliancesInEurope";
import { ImportAndExportOfHomeAppliancesInEuropeByContinent } from "../../../components/CustomPageBlocks/pillar2/ImportAndExportOfHomeAppliancesInEuropeByContinent/ImportAndExportOfHomeAppliancesInEuropeByContinent";
import { MembersManufacturingSites } from "../../../components/CustomPageBlocks/pillar2/MembersManufacturingSites/MembersManufacturingSites";
import { PatentsFiledAndGrantedForHomeAppliances } from "../../../components/CustomPageBlocks/pillar2/PatentsFiledAndGrantedForHomeAppliances/PatentsFiledAndGrantedForHomeAppliances";
import { Top10ImportAndExportCountries } from "../../../components/CustomPageBlocks/pillar2/Top10ImportAndExportCountries/Top10ImportAndExportCountries";

// --- Data ---
import TradeShareChart1Data from "../../../assets/data/content/pillar-2/trade-share-chart-1/data.json";
import TradeShareChart2Data from "../../../assets/data/content/pillar-2/trade-share-chart-2/data.json";
import TradeShareChart3Data from "../../../assets/data/content/pillar-2/trade-share-chart-3/data.json";
import EuropeanMarketHistoricalPictureData from "../../../assets/data/content/pillar-2/european-market-historical-picture/data.json";
import PatentsFiledAndGrantedForHomeAppliancesData from "../../../assets/data/content/pillar-2/patents-filed-and-granted-for-home-appliances/data.json";

// --- Marginalia ---
import { createChartMarginalia } from "../marginalia/createChartMarginalia";
import { innovationPageMarginalia } from "../marginalia/pillar2Innovation";
import { industrialFootprintPageMarginalia } from "../marginalia/pillar2IndustrialFootprint";
import { tradePageMarginalia } from "../marginalia/pillar2Trade";

// --- Utils ---
import { fetchCountryData } from "../../../utils/fetchCountryData";
import { fetchTradeData } from "../../../utils/fetchTradeData";

// --- Config ---
import { UiColors } from "../../../config/ui/colors";

// --- Types ---
import { PageContent } from "./types";

export const pillar2Content: Record<string, PageContent> = {
	"/pillar/2": {
		heroImage: BgAcceleratingEuropesGrowth,
		heroIntro: "The home appliance industry, a cornerstone of European manufacturing, plays a crucial role in driving the European economy and is essential for achieving Europe's ambitious decarbonisation goals.",
		blocks: [
			{
				id: "pillar2-intro-text",
				type: "text",
				data: {
					text: [
						"This sector directly secures an extensive domestic industrial footprint while spearheading technological advancements and cross-border trade that preserve the region's long-term global competitiveness. Europe’s economic strength is closely tied to the resilience of its manufacturing base, with the home appliance sector functioning as a strategic pillar for employment, innovation, and trade. In an increasingly complex regulatory environment and under intensifying geopolitical pressure, the integrity of a highly integrated and frictionless European Single Market is essential. Reducing national fragmentation and removing cross-border regulatory barriers are critical enablers for manufacturers seeking to scale technologies, strengthen domestic value chains, and deliver affordable energy-efficient solutions to European consumers. This chapter examines the sector’s industrial evolution across three strategic dimensions: the expansion of manufacturing footprints despite cyclical market pressures, the geographic concentration of innovation and intellectual property generation, and the shifting patterns of intra-European and global trade flows.",
					],
				},
			},
			{
				id: "pillar2-repair-text-image",
				type: "text-image",
				data: {
					type: "image-right",
					imageUrl: Pillar2Consumer,
					imageAlt: "Father and son using a tumbledryer",
					imageSize: "normal",
					text: [
						"The foundation of this development lies in a robust and geographically distributed industrial base. The sector operates an extensive network of manufacturing facilities that collectively secure Europe’s production capacity for a majority of home appliances. Maintaining this industrial footprint requires continuous adjustment to structural pressures, including volatile input costs, regulatory divergence, and evolving demand dynamics across both large and small appliance categories.","Alongside its production strength, the industry is underpinned by a highly concentrated innovation ecosystem that drives next-generation technological development. This capability reinforces the sector’s competitiveness in both intra-European and global markets, enabling complex trade flows that keep European production integrated with international demand. At the same time, the share of intra-Single Market sourcing has shown a gradual shift, reflecting the evolving geography of value creation within and beyond Europe.",
					],
				},
			},
		],
	},
	"/pillar/2/industrial-footprint": {
		pageMarginalia: industrialFootprintPageMarginalia,
		blocks: [
			{
				id: "industrial-footprint-intro",
				type: "opening-text",
				data: {
					text: "The foundation of the sector's contribution to regional economic resilience is rooted in its extensive localised manufacturing base. Operating a vast network of dedicated production facilities across the continent, the industry actively shores up supply chain stability and serves as a major employer within Europe's industrial landscape. The subsequent data mapping and volume breakdowns track an expanding manufacturing footprint, highlighting the industry's capacity to maintain robust production levels even amidst broader economic market corrections.",
				},
			},
			{
				id: "industrial-footprint-kpis",
				type: "kpi-cards",
				data: {
					items: [
						{
							value: "119",
							unit: "",
							comparison: "",
							description: "Direct Members Manufacturing sites in Europe",
							icon: IconFactory,
						},
						{
							value: "172,2",
							unit: "million",
							comparison: "",
							description: "Number of home appliances sold in Europe",
							icon: IconShoppingCart,
						},
						{
							value: "66,2",
							unit: "million",
							comparison: "",
							description: "Number of small appliances traded in Europe",
							icon: IconPackage,
						},
					],
				},
			},
			{
				type: "text",
				id: "industrial-footprint-manufacturing-sites-text",
				data: {
					text: [
						"The foundation of the sector's contribution to continental growth is rooted in its extensive localised manufacturing base. In 2025, the network tracked <strong>119 manufacturing facilities</strong> operated by APPLiA Direct Members across Europe. A country-by-country mapping reveals a <strong>highly concentrated industrial baseline</strong>, with Poland and Germany serving as the largest production centers, followed closely by Italy and France."
					],
				},
			},
			{
				type: "custom",
				id: "industrial-footprint-manufacturing-sites",
				chartLabel: "Chart 2.1",
				data: {
					component: MembersManufacturingSites,
				},
				marginalia: createChartMarginalia("APPLiA members data for 2025", "industrial-footprint-manufacturing-sites", "applia-home-appliance-monitor-2025_applia-members-manufacturing-sites.png", { label: "CHART 2.1" }),
			},
			{
				id: "industrial-footprint-market-text-chart",
				type: "text-chart",
				chartLabel: "Chart 2.2",
				data: {
					text: [
						"The evolution of the appliance market reflects a period of notable consumer recalibration. In 2025, <strong>total sales in Europe reached 172.2 million units</strong>, marking a 1.7% decline compared to the previous year. Large appliances remained broadly stable, with volumes decreasing only slightly by 0.3% to 106.0 million units. In contrast, small appliances experienced a more pronounced contraction, falling by 3.7% to 66.2 million units. This decline in small appliances was mainly driven by a sharp 21.1% drop in food preparation devices, which fell to 14.6 million units. This strong downturn offset steady growth in standard coffee machines, which continued to perform positively within the segment.",
					],
					chartPosition: "right",
					component: EuropeanMarketHistoricalPicture,
					props: {
						data: EuropeanMarketHistoricalPictureData,
					},
				},
				marginalia: createChartMarginalia("APPLiA members data", "industrial-footprint-market-text-chart", "applia-home-appliance-monitor-2025_historical-evolution-of-the-home-appliance-market.png", { label: "CHART 2.2" }),
			},
			{
				type: "header",
				id: "industrial-footprint-units-traded-header",
				chartLabel: "Chart 2.3",
				data: {
					type: "h3",
					text: "Units traded in Europe",
					showChartLabel: true
				},
			},
			{
				type: "responsive-image",
				id: "industrial-footprint-units-traded-intro",
				data: { desktopImageUrl: UnitsTradedIntroDesktop, mobileImageUrl: UnitsTradedIntroMobile, desktopClassName: "w-full max-w-3xl" },
				marginalia: createChartMarginalia("APPLiA members data", "industrial-footprint-units-traded-header", "applia-home-appliance-monitor-2025_units-traded-in-europe.png", { label: "CHART 2.3" })
			},
			{
				type: "responsive-image",
				id: "industrial-footprint-units-traded-section1",
				data: { desktopImageUrl: UnitsTradedSection1Desktop, mobileImageUrl: UnitsTradedSection1Mobile, desktopClassName: "w-full max-w-3xl" },
			},
			{
				type: "responsive-image",
				id: "industrial-footprint-units-traded-section2",
				data: { desktopImageUrl: UnitsTradedSection2Desktop, mobileImageUrl: UnitsTradedSection2Mobile, desktopClassName: "w-full max-w-3xl" },
			},
			{
				id: "industrial-footprint-text-1",
				type: "text",
				data: {
					text: [
						"When tracking the absolute origin of home appliances entering regional markets, <strong>China stands as the undisputed leading supplier</strong>, providing an immense volume of products to European consumers. Immediately following China is Germany, cementing its role as the dominant manufacturing and distribution pivot within the European continent itself.","A mirrored pattern defines the primary destinations for appliance shipments moving out of European factories. <strong>Internal European trade heavily dictates the export landscape</strong>, with major continental markets like Germany and its close geographic neighbors serving as the primary drivers of demand for newly manufactured units. Outside of the immediate European territory, mature adjacent markets like the United Kingdom anchor external outbound trade. This concentrated distribution proves that the sector’s financial health is fundamentally reliant on the strength of its internal Single Market corridors.",
					],
				},
			},
			{
				type: "custom",
				id: "industrial-footprint-top10-import-export",
				chartLabel: "Chart 2.4 & 2.5",
				data: {
					component: Top10ImportAndExportCountries,
					props: {
						data: fetchCountryData,
					},
				},
				marginalia: createChartMarginalia("Eurostat COMEXT", "industrial-footprint-top10-import-export", "applia-home-appliance-monitor-2025_top-10-import-export-countries.png", { label: "CHART 2.4 & 2.5" }),
			},
		],
	},
	"/pillar/2/innovation": {
		pageMarginalia: innovationPageMarginalia,
		blocks: [
			{
				type: "opening-text",
				id: "innovation-intro",
				data: {
					text: "The long-term competitiveness of the European home appliance sector relies entirely on its continuous technological evolution. Facing intense global competition and strict regulatory demands, manufacturers must heavily fund research and development to pioneer next-generation, circular technologies. The upcoming metrics map the scale and output of this high-intensity innovation ecosystem, tracing how raw R&D funding translates directly into intellectual property and cutting-edge product features across the region.",
				},
			},
			{
				type: "kpi-cards",
				id: "innovation-kpis",
				data: {
					items: [
						{
							value: "€160,9",
							unit: "bn",
							comparison: "",
							description: "Total EU investment in R&D",
							icon: IconFlask,
						},
						{
							value: "1,23%",
							unit: "",
							comparison: "",
							description: "Average investment in R&D per EU Member State, as a fraction of GDP",
							icon: IconChartPie,
						},
						{
							value: "4.384",
							unit: "",
							comparison: "",
							description: "Number of patents granted for home appliances in 2025",
							icon: IconFileBadge,
						},
					],
				},
			},
			{
				type: "text",
				id: "innovation-investment",
				data: {
					text: "Sustaining competitiveness amid shifting market conditions requires sustained, long-term investment in technology. <strong>Total R&D expenditure in the European Union reached €160.9 billion in 2025</strong>, a 1.4% year-on-year increase. However, this investment remains highly concentrated geographically. Germany accounts for 34.5% of total EU R&D spending in the sector, followed by France (15.7%) and Italy (6.5%).",
				},
			},
			{
				type: "responsive-image",
				id: "innovation-rd-by-member-states",
				chartLabel: "Chart 2.6",
				data: {
					desktopImageUrl: InvestmentRDDesktop,
					mobileImageUrl: InvestmentRDMobile,
					imageAlt: "Treemap that shows investment in R&D by EU Member States. Germany: 35,7%. France: 16,3%. Italy: 6,5%. The Netherlands: 6,2%. Belgium: 5,9%. Sweden: 5,8%. Spain: 5,1%. The other countries are below 5%.",
					title: "Investment in R&D by EU Member States",
					breakpoint: "sm",
					desktopClassName: "w-full max-w-6xl",
					mobileClassName: "w-full max-w-md self-start",
				},
				marginalia: createChartMarginalia("Eurostat (RD_E_GERDTOT) R&D related to business sector, 2024 data", "innovation-rd-by-member-states", "applia-home-appliance-monitor-2025_investment-in-rd-by-eu-member-states.png", { label: "CHART 2.6" }),
			},
			{
				type: "text",
				id: "innovation-investment",
				data: {
					text: "When adjusted for economic size, clear <strong>regional differences</strong> emerge. The highest R&D intensity is concentrated in North-Western Europe, led by Sweden (2.61% of GDP), Belgium (2.43%), and Austria (2.25%), while countries with strong manufacturing bases such as Poland (0.89%) and Italy (0.79%) remain significantly lower.",
				},
			},
			{
				type: "responsive-image",
				id: "innovation-rd-vs-gdp-image",
				chartLabel: "Chart 2.7",
				data: {
					desktopImageUrl: InvestmentGDPDesktop,
					mobileImageUrl: InvestmentGDPMobile,
					title: "Investment in R&D as fraction of GDP",
				},
				marginalia: createChartMarginalia("Eurostat (RD_E_GERDTOT) R&D related to business sector, 2024 data", "innovation-rd-vs-gdp", "applia-home-appliance-monitor-2025_investment-in-rd-as-fraction-of-gdp.png", { label: "CHART 2.7" }),
			},
			{
				id: "innovation-patents-2",
				type: "text-chart",
				chartLabel: "Chart 2.8",
				data: {
					text: [
						"This investment pattern is reflected in intellectual property output. Patent filings at the European Patent Office (EPO) increased steadily over the past decade, rising from around 10,000 in 2012 to a peak of 15,558. Following recent economic disruptions, filings have eased to 13,482, while the number of patents granted has stabilised at roughly 4,700 per year over the past two years. Overall, this suggests a shift from rapid expansion in innovation output toward a more <strong>stable phase of technological development</strong> across Europe’s industrial base.",
					],
					chartPosition: "right",
					textVerticalAlign: "top",
					component: PatentsFiledAndGrantedForHomeAppliances,
					props: {
						data: PatentsFiledAndGrantedForHomeAppliancesData,
					},
				},
				marginalia: createChartMarginalia("dss+", "recycling-weee-flow-text-chart", "applia-home-appliance-monitor-2025_weee-flows-in-the-home-appliance-sector.png", { label: "CHART 2.8" }),
			},
		],
	},
	"/pillar/2/trade": {
		pageMarginalia: tradePageMarginalia,
		blocks: [
			{
				id: "trade-intro",
				type: "opening-text",
				data: {
					text: "The financial health of Europe's home appliance sector is deeply tied to its ability to balance robust international commerce with seamless regional trade. As supply chains become more complex and globalised, maintaining open trade channels ensures that European factories can source essential components while successfully exporting premium, high-efficiency appliances to external markets. The upcoming metrics map out these cross-border economic flows, highlighting both Europe’s major international trading partners and a gradual structural shift in internal sourcing across the Single Market.",
				},
			},
			{
				id: "trade-kpis",
				type: "kpi-cards",
				data: {
					items: [
						{
							value: "82,2",
							unit: "million units",
							comparison: "",
							description: "Large appliances sold globally from Europe",
							icon: IconGlobe,
						},
						{
							value: "€42,6",
							unit: "bn",
							comparison: "",
							description: "Total value of exported appliances",
							icon: IconShip,
						},
						{
							value: "71,6%",
							unit: "",
							comparison: "",
							description: "Share of imported large home appliances coming from within Europe",
							icon: IconMap,
						},
					],
				},
			},
			{
				type: "responsive-image",
				id: "trade-large-appliances-sold-globally",
				chartLabel: "Chart 2.9",
				data: {
					desktopImageUrl: LargeAppliancesDesktop,
					mobileImageUrl: LargeAppliancesMobile,
					imageAlt: "Large appliances sold globally",
					breakpoint: "sm",
					desktopClassName: "w-full max-w-3xl",
					title: "Large appliances sold globally"
				},
				marginalia: createChartMarginalia("International Roundtable of Household Appliance Manufacturer Associations (IRHMA)","trade-large-appliances-sold-globally", "applia-home-appliance-monitor-2025_large-appliances-sold-globally.png", { infoBox: { title: "Info", body: "Product groups included: refrigerators, freezers, dishwashers, tumble dryers, hoods, and microwave ovens." },  label: "CHART 2.9" }),
			},
			{
				id: "trade-text-1",
				type: "text",
				data: {
					text: "The economic strength of the European appliance sector is closely linked to <strong>integrated intercontinental trade networks</strong>. Total imports of home appliances into Europe reached €51.648 billion, with small appliances accounting for the larger share at €29.762 billion, compared to €21.886 billion for large appliances. Asia remains the primary source of imports, contributing €16.906 billion to the EU market.",
				},
			},
			{
				type: "custom",
				id: "trade-import-export-by-continent",
				chartLabel: "Chart 2.10 & 2.11",
				data: {
					component: ImportAndExportOfHomeAppliancesInEuropeByContinent,
					props: {
						data: fetchTradeData,
					},
				},
				marginalia: createChartMarginalia("Eurostat COMEXT", "trade-import-export-by-continent", "applia-home-appliance-monitor-2025_import-and-export-of-home-appliances-by-continent.png", { label: "CHART 2.10 & 2.11" }),
			},
			{
				id: "trade-text-2",
				type: "text",
				data: {
					text: "On the export side, European shipments grew by 8.0% to €42.634 billion, driven mainly by small appliances, which accounted for €24.132 billion of total exports.",
				},
			},
			{
				type: "custom",
				id: "trade-evolution-import-export",
				chartLabel: "Chart 2.12 & 2.13",
				data: {
					component: EvolutionOfImportAndExportOfHomeAppliancesInEurope,
					props: {
						data: fetchTradeData,
					},
				},
				marginalia: createChartMarginalia("Eurostat COMEXT", "trade-evolution-import-export", "applia-home-appliance-monitor-2025_evolution-of-import-and-export-of-home-appliances.png", { label: "CHART 2.12 & 2.13" }),
			},
			{
				id: "trade-text-final",
				type: "text",
				data: {
					text: [
						"Despite overall growth in trade volumes, long-term sourcing patterns indicate a gradual decline in intra-European trade share. The proportion of imports sourced within Europe has fallen across all product categories. For large appliances, <strong>intra-European sourcing declined to 71.6%</strong>, a decrease of 5.1 percentage points. Similar trends are visible in small appliances (64.2%) and home comfort appliances (63.9%).","Overall, this shift points to a <strong>growing reliance on external supply chains</strong> and highlights the importance of reducing internal Single Market frictions to support Europe’s industrial base.",
					],
				},
			},
			{
				type: "custom",
				id: "trade-share-chart-1",
				chartLabel: "Chart 2.14a",
				data: {
					component: TradeShareLineChart,
					props: {
						data: TradeShareChart1Data,
						color: UiColors.charts.primary,
						yAxisLabel: "Share of large home appliances imported from within Europe",
						chartSubtitle: "Ratio of intra imports versus intra + outside imports, in %",
					},
				},
				marginalia: createChartMarginalia("Proxy local share = Intra-area exports ÷ (Intra-area exports + Imports from outside area) - APPLiA elaboration on Eurostat data.", "trade-share-chart-1", "applia-home-appliance-monitor-2025_share-of-large-home-appliances-imported-from-within-europe.png", { label: "CHART 2.14a" }),
			},
			{
				type: "custom",
				id: "trade-share-chart-2",
				chartLabel: "Chart 2.14b",
				data: {
					component: TradeShareLineChart,
					props: {
						data: TradeShareChart2Data,
						color: UiColors.charts.secondary,
						yAxisLabel: "Share of small home appliances imported from within Europe",
						chartSubtitle: "Ratio of intra imports versus intra + outside imports, in %",
					},
				},
				marginalia: createChartMarginalia("Proxy local share = Intra-area exports ÷ (Intra-area exports + Imports from outside area) - APPLiA elaboration on Eurostat data.", "trade-share-chart-2", "applia-home-appliance-monitor-2025_share-of-small-home-appliances-imported-from-within-europe.png", { label: "CHART 2.14b" }),
			},
			{
				type: "custom",
				id: "trade-share-chart-3",
				chartLabel: "Chart 2.14c",
				data: {
					component: TradeShareLineChart,
					props: {
						data: TradeShareChart3Data,
						color: UiColors.charts.tertiary,
						yAxisLabel: "Share of home comfort appliances imported from within Europe",
						chartSubtitle: "Ratio of intra imports versus intra + outside imports, in %",
					},
				},
				marginalia: createChartMarginalia("Proxy local share = Intra-area exports ÷ (Intra-area exports + Imports from outside area) - APPLiA elaboration on Eurostat data.", "trade-share-chart-3", "applia-home-appliance-monitor-2025_share-of-home-comfort-appliances-imported-from-within-europe.png", { label: "CHART 2.14c" }),
			},
		],
	},
};
